/**
 * FloorPlanTemplateSelection — native lift of the legacy
 * `FloorPlanTemplateTypeSelection` + `FloorPlanTemplates` (saved-templates grid +
 * "create new"). Data wiring is rewritten onto the ops-hook callbacks; no constate.
 *
 * The logo-uploading guard disables the grid + "create new" while a logo
 * transfers (`logoUploading`), standing in for the dropped live
 * `isTemplateLogoUploading` completeness term.
 */

import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ReactLoading from 'react-loading'
import { useTranslation } from 'react-i18next'

import { BEIGE_500, CORAL_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Color } from 'constants/assets'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'

import { FloorPlanRenderType, FloorPlanSelectionType } from 'constants/floorPlan'
import { SelectableImageBox } from 'components/common/FloorPlanControls'
import type { CompositeTemplate } from '../useFloorPlanConfigTemplates'
import type { FloorPlanConfigOptions } from 'constants/floorPlan'

interface Props {
  availableTemplates: Record<string, CompositeTemplate>
  isFetching: boolean
  selectedTemplateId: string | null | undefined
  floorPlanTemplateType: FloorPlanSelectionType | null | undefined
  logoUploading: boolean
  applyTemplate: (templateId: string, config: FloorPlanConfigOptions, renderType: FloorPlanRenderType) => void
  createFromScratch: () => void
}

/**
 * @component FloorPlanTemplateSelection
 * Saved-template grid + "create new" entry point.
 */
export const FloorPlanTemplateSelection: React.FC<Props> = ({
  availableTemplates,
  isFetching,
  selectedTemplateId,
  floorPlanTemplateType,
  logoUploading,
  applyTemplate,
  createFromScratch,
}) => {
  const { t } = useTranslation(['purchase_flow', 'common'])

  return (
    <BorderBoxWrapper backgroundColor={BEIGE_500} padding="2.4rem" elevation="none" border="none">

      <Stack alignItems="center" position="relative">

        <Typography variant="text-md" fontWeight={600} color={GRAY_900}>
          {t('floor_plan_step.templates.title')}
        </Typography>

        <Stack
          marginTop={2}
          direction="row"
          justifyContent="center"
          alignItems="center"
          gap={2}
          sx={{ flexFlow: 'row wrap' }}
          width="100%"
        >
          {Object.values(availableTemplates).map(({ id, name, imagePlaceholder, renderType, config }) =>
            <SelectableImageBox
              key={id}
              text={name}
              width={16.8}
              height={14.4}
              disabled={logoUploading}
              elevation="xs"
              image={isFetching
                ? <Box marginTop={6}><ReactLoading color={Color.GRAY_TEXT} type="spin" width="2rem" height="2rem" /></Box>
                : imagePlaceholder}
              inactiveBorderColor="transparent"
              isSelected={id === selectedTemplateId}
              onSelect={() => !logoUploading && applyTemplate(id, config, renderType)}
            />
          )}
        </Stack>

        {!floorPlanTemplateType &&
          <Typography variant="text-xs" fontWeight={500} color={CORAL_600} marginTop={1}>
            {t('floor_plan_step.templates.template_required_error')}
          </Typography>
        }

      </Stack>

      <MUIDivider margin={16}>
        <Typography variant="text-sm" color={GRAY_700}>
          {t('common:Or')}
        </Typography>
      </MUIDivider>

      <Stack alignItems="center">
        <MUIButton
          type="secondaryBorder"
          disabled={logoUploading}
          onClick={() => createFromScratch()}
          startIcon={<AddIcon fontSize="large" />}
        >
          {t('floor_plan_step.templates.new_template')}
        </MUIButton>
      </Stack>

    </BorderBoxWrapper>
  )
}
